import * as React from "react";
import {
    List,
    Edit,
    SimpleForm,
    Datagrid,
    TextField,
    ReferenceField,
    NumberField,
    DateField,
    FunctionField,
    Filter,
    FilterList, FilterListItem,
    TextInput,
    NumberInput,
    DateInput,
    DateTimeInput,
    ReferenceInput,
    SelectInput,
    TopToolbar, Show, EditButton,
    TabbedShowLayout, Tab,
    useRecordContext, Pagination, useGetList
} from 'react-admin';
import {
    Card as MuiCard,
    CardContent,
    Typography,
    Grid,
    Paper
} from '@mui/material';
import {
    DeleteSweepOutlined as DeleteSweepOutlinedIcon,
    LocalOfferOutlined as LocalOfferOutlinedIcon,
    PlaylistAddCheckOutlined as PlaylistAddCheckOutlinedIcon,
    LocalShipping as LocalShippingIcon,
    History as HistoryIcon
} from '@mui/icons-material';

import { withStyles } from '@mui/styles';
import { makeStyles } from '@mui/styles';

import {AbfallartIcon, PaperContactMail, PaperCalendarDay, InlineLoading} from "./index";
import { date2DateTimeString } from "./index";

const anmeldungFilterAbholgebietOptionRenderer = (props) => `${props.anmerkung} - ${props.name}`;
const AnmeldungFilter = (props) => (
    <Filter {...props}>
        <TextInput label="Id" source="id" alwaysOn/>
        <TextInput label="Nachname" source="nachname" alwaysOn/>
        <TextInput label="Straße" source="strasse" alwaysOn/>
        <TextInput label="Hausnummer" source="hausnummer" alwaysOn/>
        <DateInput label="Abholtermin" source="termin" alwaysOn/>
        <ReferenceInput label="Abholgebiet" source="gebiet" reference="abholgebiete" filter={{landkreis: 2}}
                        sort={{field: 'name', order: 'ASC'}} perPage={99} allowEmpty>
            <SelectInput optionText={anmeldungFilterAbholgebietOptionRenderer} optionValue="id"/>
        </ReferenceInput>
    </Filter>
);

const FilterAbholungen = () => (
    <FilterList
        label="Abholungen"
        icon={<LocalShippingIcon/>}
    >
        <FilterListItem
            label="vergangene"
            value={{
                'termin[strictly_before]': new Date().toISOString().substr(0, 10)
            }}
        />
        <FilterListItem
            label="aktuelle"
            value={{
                'termin': new Date().toISOString().substr(0, 10)
            }}
        />
        <FilterListItem
            label="künftige"
            value={{
                'termin[strictly_after]': new Date().toISOString().substr(0, 10)
            }}
        />
    </FilterList>
);

const FilterAbfallart = () => (
    <FilterList
        label="Abfallart"
        icon={<DeleteSweepOutlinedIcon/>}
    >
        <FilterListItem
            label="Sperrmüll"
            value={{
                abfallart: 5
            }}
        />
        <FilterListItem
            label="Elektro"
            value={{
                abfallart: 6
            }}
        />
    </FilterList>
);

const FilterStatus = () => (
    <FilterList
        label="Status"
        icon={<LocalOfferOutlinedIcon/>}
    >
        <FilterListItem
            label="nicht storniert"
            value={{
                'cancelled[exists]': false
            }}
        />
        <FilterListItem
            label="storniert"
            value={{
                'cancelled[exists]': true
            }}
        />
    </FilterList>
);

const FilterToDoList = () => (
    <FilterList
        label="ToDo-Liste"
        icon={<PlaylistAddCheckOutlinedIcon/>}
    >
        <FilterListItem
            label="Anmerkungen"
            value={{
                'anmerkungen[exists]': true
            }}
        />
        <FilterListItem
            label={<GetLabelCountOhneTermin />}
            value={{
                'termin[exists]': false
            }}
        />
    </FilterList>
);

const GetLabelCountOhneTermin = () => {
    const {total, data, isLoading, error} = useGetList(
        'anmeldungen',
        {
            pagination: {
                page: 1,
                perPage: 9999
            },
            filter: {'termin[exists]': false}
        }
    );

    if (isLoading) return <InlineLoading/>;
    if (error) return null;
    if (!data) return null;

    return (
       <span>Ohne Termin
          <span style={{
              'background': '#d32f2f',
              'color': 'white',
              'padding': '0 5px',
              'marginLeft': '5px',
              'float': 'right',
              'borderRadius': '3px'
          }}>{total}</span>
       </span>
    )
};

const Card = withStyles(theme => ({
    root: {
        [theme.breakpoints.up('sm')]: {
            order: -1, // display on the left rather than on the right of the list
            width: '15em',
            marginRight: '1em',
        },
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
}))(MuiCard);

const FilterSidebar = (props) => (
    <Card>
        <CardContent>
            <FilterAbholungen/>
            <FilterAbfallart/>
            <FilterStatus/>
            <FilterToDoList/>
        </CardContent>
    </Card>
);

const anmeldungenRowStyle = (record, index) => ({
    //https://material.io/design/color/the-color-system.html
    borderLeftColor: null != record.cancelled ? '#B81F16' : (new Date(record.termin) < new Date() ? '#72C35C' : 'white')
});

const AnmeldungList = (props) => {
    return (
        <List title={"Anmeldungen"}
              filters={<AnmeldungFilter/>}
              aside={<FilterSidebar/>}
              sort={{field: 'inserted', order: 'DESC'}}
              pagination={<Pagination rowsPerPageOptions={[10, 15, 20]} {...props} />}
              exporter={false}
              {...props}
        >
            <Datagrid rowClick="edit"
                      sx={{
                          '& .RaDatagrid-headerCell': {
                              fontWeight: 'bold',
                          },
                          '& .RaDatagrid-row': {
                              borderLeftWidth: '5px',
                              borderLeftStyle: 'solid',
                              borderLeftColor: '#fff'
                          },
                      }}
                      rowStyle={anmeldungenRowStyle}
                      expand={<AnmeldungShow/>}>
                <FunctionField label="Name" render={record => `${record.vorname} ${record.nachname}`}
                               sortBy={'nachname'}/>
                <FunctionField label="Straße" render={record => `${record.strasse} ${record.hausnummer}`}
                               sortBy={'strasse'}/>
                <FunctionField label="Ort" render={record => `${record.plz} ${record.ort}`} sortBy={'ort'}/>
                <ReferenceField source={"abfallart"} reference={"abfallarten"}>
                    <TextField source="name"/>
                </ReferenceField>
                <DateField source={"termin"}
                           options={{weekday: 'short', year: 'numeric', month: 'long', day: 'numeric'}}/>
                <NumberField source={"menge"}/>
                <TextField source={"anmerkungen"}/>
                <DateField label="Anmeldedatum" source={"inserted"} options={{
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: 'numeric',
                    minute: 'numeric'
                }}/>
            </Datagrid>
        </List>
    )
};

const AnmeldungShowTitle = (props) => {
    return <span> | {props.vorname} {props.nachname}, {props.strasse} {props.hausnummer}, {props.plz} {props.ort}</span>;
}

const typoStyles = makeStyles({
    textRight: {
        float: 'right'
    },
    label: {
        color: '#999'
    }
});

const gridStyles = makeStyles({
    root: {
        flexGrow: 1
    },
});

const Aside = (props) => {
    const record = useRecordContext(props);
    const classes = typoStyles();

    var inserted = date2DateTimeString(record.inserted);
    var cancelled = date2DateTimeString(record.cancelled);
    var confirmed = (record.bestaetigung == 'on' ? 'E-Mail' : '--');

    return (
        <div>
            <Paper elevation={2} sx={{width: 330, margin: '1em', backgroundColor: '#fcfcfc', marginTop: 0, padding: '20px'}}>
                <Grid container spacing={3}>
                    <Grid item xs={2}>
                        <HistoryIcon color="info" fontSize="large" />
                    </Grid>
                    <Grid item xs={10}>
                        <Typography variant="h6">Historie</Typography>
                        <br/>
                        <Typography variant="body2">
                            <b>angemeldet <span className={classes.textRight}>Bestätigung</span></b><br/>
                            {inserted} <span className={classes.textRight}>{confirmed}</span>
                            <br/>
                            <br/>
                            <b>durch</b><br/>
                            {record.inserted_by}
                            <br/>
                            <br/>
                            <b>storniert</b><br/>
                            {cancelled}
                            <br/>
                            <br/>
                            <b>durch</b><br/>
                            {record.cancelled_by}
                            <br/>
                            <br/>
                            <b>Anmerkungen intern</b><br/>
                            {record.anmerkungenIntern}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </div>
    );
};

const AnmeldungFastEdit = (props) => {
    const record = useRecordContext(props);
    const classes = typoStyles();
    const gridClasses = gridStyles();

    return (
        <div className={gridClasses.root}>
            <Grid container spacing={3}>
                <Grid item xs>
                    <Typography variant="body1"><b>Abholung</b></Typography>
                    <DateInput variant="outlined" source={"termin"} style={{width: '75%'}}/><br/>
                    <NumberInput variant="outlined" source={"menge"} label="Menge" min={.5} max={5} step={.5} style={{width: '75%'}}/>
                    <TextInput variant="outlined" label="Anmerkungen" source="anmerkungen" style={{width: '100%'}}/>
                </Grid>
                <Grid item xs>
                    <Typography variant="body1"><b>Storno</b></Typography>
                    <DateTimeInput variant="outlined" source={"cancelled"} label="Stornodatum" style={{width: '75%'}}/><br/>
                    <TextInput variant="outlined" source={"cancelledBy"} label="Storno durch" style={{width: '75%'}}/>
                </Grid>
                <Grid item xs>
                    <Typography variant="body1"><b>Kontakt</b></Typography>
                    <TextInput variant="outlined" source={"email"} label="E-Mail" style={{width: '100%'}}/>
                    <TextInput variant="outlined" source={"telefon"} label="Telefon" style={{width: '100%'}}/>
                </Grid>
            </Grid>
        </div>
    );
};

const showStyles = makeStyles({
    tabbedLayout: {
        marginBottom: '1em'
    }
});

const AbholgebietReference = (props) => {
    return (
        <ReferenceField source={"gebiet"} reference={"abholgebiete"} {...props}>
            <FunctionField render={(record) => {
                return (
                    <span>{record.name}</span>
                )
            }} />
        </ReferenceField>
    )
}

const StrasseReference = (props) => {
    return (
        <ReferenceField source={"strasseRelation"} reference={"strassen"} {...props}>
            <FunctionField render={(record) => {
                return (
                    <span>{record.ortsteil}</span>
                )
            }} />
        </ReferenceField>
    )
}

const AnmeldungSummary = (props) => {
    const record = useRecordContext({...props});

    return (
        <Grid container spacing={2}>
            <Grid item xs={6}>
                <Paper elevation={3} sx={{
                    p: 2,
                    background: '#2872b7',
                    color: '#fff',
                    textAlign: 'left'
                }}>
                    <Typography variant={"h4"}>
                        {record.menge ? record.menge : 0}
                    </Typography>
                    <Typography variant={"caption"}>
                        Menge in m³
                    </Typography>
                </Paper>
            </Grid>
            <Grid item xs={6}>
                <Paper elevation={3} sx={{
                    p: 2,
                    background: '#2872b7',
                    color: '#fff',
                    textAlign: 'center'
                }}>
                    <AbfallartIcon {...props}/>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <PaperCalendarDay {...props}/>
            </Grid>
        </Grid>
    )
}

const AnmeldungShow = (props) => {
    const record = useRecordContext({...props});
    const showClasses = showStyles();
    const classes = typoStyles();

    return (

        <Show emptyWhileLoading title={<AnmeldungShowTitle/>} className={showClasses.tabbedLayout}
              aside={<Aside/>} /* " / Anmeldung" actions={<ShowActions />}*/ {...props}>
            <TabbedShowLayout syncWithLocation={false}>
                <Tab label="Adresse">
                    <Grid container spacing={2} p={1}>
                        <Grid item xs={8}>
                            <PaperContactMail {...props}/>
                        </Grid>
                        <Grid item xs={4}>
                            <AnmeldungSummary {...props}/>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant={"caption"} className={classes.label}>Id</Typography>
                            <Typography variant={"body2"}>{record.id}</Typography>
                        </Grid>
                    </Grid>
                </Tab>
                <Tab label="Abholung">
                    <Grid container spacing={2} p={1}>
                        <Grid item xs={8}>
                            <Grid container spacing={3}>
                                <Grid item xs>
                                    <Typography variant="caption" className={classes.label}>Gebiet</Typography>
                                    <Typography variant={"body2"}><AbholgebietReference {...props}/></Typography>
                                    <Typography variant="caption" className={classes.label}>Ortsteil</Typography>
                                    <Typography variant={"body2"}><StrasseReference {...props}/></Typography>
                                </Grid>
                                <Grid item xs>
                                    <Typography variant="body1"><b>Sperrmüll</b></Typography>
                                    <Typography variant="caption" className={classes.label}>Möbel</Typography>
                                    <Typography variant="body2">{record.mengeMoebel}</Typography>
                                    <Typography variant="caption" className={classes.label}>Polstermöbel</Typography>
                                    <Typography variant="body2">{record.mengePolstermoebel}</Typography>
                                    <Typography variant="caption" className={classes.label}>Gartenmöbel</Typography>
                                    <Typography variant="body2">{record.mengeGartenmoebel}</Typography>
                                    <Typography variant="caption" className={classes.label}>Sonnenschirm</Typography>
                                    <Typography variant="body2">{record.mengeSonnenschirm}</Typography>
                                    <Typography variant="caption" className={classes.label}>Kinderwagen</Typography>
                                    <Typography variant="body2">{record.mengeKinderwagen}</Typography>
                                    <Typography variant="caption" className={classes.label}>Fahrrad</Typography>
                                    <Typography variant="body2">{record.mengeFahrrad}</Typography>
                                    <Typography variant="caption" className={classes.label}>Koffer</Typography>
                                    <Typography variant="body2">{record.mengeKoffer}</Typography>
                                    <Typography variant="caption" className={classes.label}>Matratze</Typography>
                                    <Typography variant="body2">{record.mengeMatratze}</Typography>
                                    <Typography variant="caption" className={classes.label}>Sonstiges 1 m³</Typography>
                                    <Typography variant="body2">{record.mengeSonstiges_1cbm}</Typography>
                                    <Typography variant="caption" className={classes.label}>Sonstiges 3 m³</Typography>
                                    <Typography variant="body2">{record.mengeSonstiges_3cbm}</Typography>
                                </Grid>
                                <Grid item xs>
                                    <Typography variant="body1"><b>Elektro</b></Typography>
                                    <Typography variant="caption" className={classes.label}>Waschmaschine</Typography>
                                    <Typography variant="body2">{record.mengeWaschmaschine}</Typography>
                                    <Typography variant="caption" className={classes.label}>Tv</Typography>
                                    <Typography variant="body2">{record.mengeTv}</Typography>
                                    <Typography variant="caption" className={classes.label}>Mikrowelle</Typography>
                                    <Typography variant="body2">{record.mengeMikrowelle}</Typography>
                                    <Typography variant="caption" className={classes.label}>Sonstiges Elektro</Typography>
                                    <Typography variant="body2">{record.mengeSonstigeElektro}</Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <AnmeldungSummary {...props}/>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant={"caption"} className={classes.label}>Anmerkungen</Typography>
                            <Typography variant={"body2"}>{record.anmerkungen}</Typography>
                        </Grid>
                    </Grid>
                </Tab>
                <Tab label="Bearbeiten" style={{position: 'absolute', right: 0}}>
                    <Edit title={" / Bearbeiten"} className={showClasses.tabbedLayout} actions={false} {...props}>
                        <SimpleForm>
                            <AnmeldungFastEdit {...props}></AnmeldungFastEdit>
                            <Typography variant="body1"><b>Anmerkungen intern</b></Typography>
                            <TextInput variant="outlined" label="Anmerkungen intern" source="anmerkungenIntern" style={{width: '100%'}}/>
                        </SimpleForm>
                    </Edit>
                </Tab>
            </TabbedShowLayout>
        </Show>
    );
};

export {AnmeldungList, AnmeldungShow};
